/**
 * **估值曲线**（R 域 `AC-R2.5` · 端侧绘制层）
 *
 * 依据：`AC-R2.5`（只用真实点）· `DR-053` **D20** · J 域 `DR-012` 同则（禁估算/平推/插值）。
 *
 * ## 🔴 本组件的**全部职责 = 把「只用真实点」落在绘制层**
 *
 * | # | 约束 | 违反后用户看到什么 |
 * |:--:|---|---|
 * | 1 | 🔴 **点 = 服务端下发的每一条估值记录**，一个不增一个不减 | 曲线比实际"更平滑"，而平滑来自端侧编造 |
 * | 2 | 🔴 **不插值**：折线只连**相邻两个真实点**，两点之间不计算任何中间值 | 用户以为中间那些日期"估过值" |
 * | 3 | 🔴 **0 点不画图、1 点不画线** | 一个点连出一条"水平线" ≈ 告诉用户"这一年没变"（那是编的） |
 *
 * ## ⚠️ 为什么手写 SVG 而不是 Recharts
 *
 * ① `LineChart` 默认会做 `monotone` 平滑 —— 那正是约束 2 禁止的东西（`NetWorthChart`
 * 用 `type="linear"` 规避过，但那是"记得改一个 prop"级别的防线）；
 * ② 单点时 Recharts 仍会画出一条线段状元素，**画了没有的线**；
 * ③ 手写后"有没有折线"是**元素存在性**，spec 可以直接数 `<polyline>` 的个数 ——
 * 可断言性远高于查 canvas/内部 DOM。
 *
 * ## ⚠️ 坐标算术不算业务算术
 *
 * 这里确实有除法（把分值映射到像素），走内核 `divideExact()` —— 端侧 `BinaryExpression[operator='/']`
 * 被 ESLint 全封（`AC-P1.2`），而绘制层的比例换算属于"确属非金额场景"，
 * 用内核函数比 `eslint-disable` 更干净。
 */

import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  centsToYuanText,
  divideExact,
  formatMoneyText,
  type Cents,
  type MajorAssetValuationView,
} from '@jiaying/shared';
import { curveCountTextOf, curveStateOf } from '../features/major-asset.js';
import { CURVE_EMPTY_HINT, CURVE_SINGLE_HINT } from '../features/major-asset-labels.js';

/** 画布（viewBox 尺寸与原型 `16-重大资产_原型.html:330` 一致） */
const W = 560;
const H = 190;
const PAD_L = 58;
const PAD_R = 24;
const PAD_T = 22;
const PAD_B = 35;

const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

export interface MajorAssetCurveProps {
  /** 🔴 **服务端下发的全部真实点**（升序）—— 组件不筛、不补、不排序 */
  points: readonly MajorAssetValuationView[];
  currency: string;
}

/** 一个点的像素坐标 */
interface Px {
  x: number;
  y: number;
  date: string;
  text: string;
}

/**
 * 估值 → 像素。`min === max` 时压到垂直居中（否则分母为 0）。
 *
 * 🔴 这里**不做"美化"**：不加缓冲、不对齐刻度 —— 曲线形状只由真实点决定。
 */
function toPixels(points: readonly MajorAssetValuationView[]): Px[] {
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const lastIndex = points.length - 1;
  return points.map((p, i) => ({
    x: PAD_L + (lastIndex === 0 ? 0 : divideExact(i * PLOT_W, lastIndex)),
    y: span === 0 ? PAD_T + divideExact(PLOT_H, 2) : PAD_T + PLOT_H - divideExact((p.value - min) * PLOT_H, span),
    date: p.valuationDate,
    text: centsToYuanText(p.value as Cents),
  }));
}

export function MajorAssetCurve({ points, currency }: MajorAssetCurveProps) {
  const theme = useTheme();
  const state = curveStateOf(points.length);
  const px = toPixels(points);
  const first = points[0];
  const last = points[points.length - 1];

  // 区间变化（**来自服务端下发的两个真实点**，不是趋势外推）
  const deltaText =
    points.length >= 2 && first !== undefined && last !== undefined
      ? formatMoneyText((last.value - first.value) as Cents, currency, { signed: true })
      : null;

  return (
    <Box data-testid="ma-curve">
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          估值曲线
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          只画真实点 · 禁插值 / 平推 / 估算
        </Typography>
        <Typography variant="caption" color="text.secondary" data-testid="ma-curve-count">
          {curveCountTextOf(points.length)}
        </Typography>
      </Stack>

      {state === 'empty' && (
        <Box
          data-testid="ma-curve-empty"
          sx={{ py: 5, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            还没有估值记录
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {CURVE_EMPTY_HINT}
          </Typography>
        </Box>
      )}

      {state !== 'empty' && (
        <Box sx={{ width: '100%' }}>
          <svg
            viewBox={`0 0 ${String(W)} ${String(H)}`}
            width="100%"
            role="img"
            aria-label="估值曲线"
            data-testid="ma-curve-svg"
          >
            {/* 坐标轴 */}
            <line
              x1={PAD_L}
              y1={PAD_T + PLOT_H}
              x2={W - PAD_R}
              y2={PAD_T + PLOT_H}
              stroke={theme.jiaying.border.base}
              strokeWidth={1}
            />
            <line
              x1={PAD_L}
              y1={PAD_T}
              x2={PAD_L}
              y2={PAD_T + PLOT_H}
              stroke={theme.jiaying.border.base}
              strokeWidth={1}
            />

            {/*
              🔴 折线**只在 ≥2 点时渲染**（`AC-R2.5`：1 点只画点）。
              且 `polyline` 的点序列 = 真实点序列本身 ⇒ 相邻两点之间不存在任何绘制值。
            */}
            {state === 'multi' && (
              <polyline
                data-testid="ma-curve-line"
                points={px.map((p) => `${String(p.x)},${String(p.y)}`).join(' ')}
                fill="none"
                stroke={theme.jiaying.chart[0]}
                strokeWidth={2.2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}

            {/* 每个真实点一个圆（数量 = 服务端下发点数） */}
            {px.map((p, i) => (
              <circle
                key={`${p.date}-${String(i)}`}
                data-testid="ma-curve-dot"
                cx={p.x}
                cy={p.y}
                r={4.2}
                fill={theme.palette.background.paper}
                stroke={theme.jiaying.chart[0]}
                strokeWidth={2}
              />
            ))}

            {/* 当前估值点 = 最后一个真实点，高亮 */}
            {px.length > 0 && (
              <circle
                data-testid="ma-curve-current"
                cx={px[px.length - 1]?.x ?? 0}
                cy={px[px.length - 1]?.y ?? 0}
                r={6}
                fill={theme.palette.success.main}
              />
            )}

            {/* 首尾日期标注（中间点不标：挤在一起反而读不出来） */}
            {px.length > 0 && (
              <text
                x={px[0]?.x ?? 0}
                y={H - 8}
                textAnchor="middle"
                fontSize={10}
                fill={theme.jiaying.text.tertiary}
              >
                {px[0]?.date ?? ''}
              </text>
            )}
            {px.length > 1 && (
              <text
                x={px[px.length - 1]?.x ?? 0}
                y={H - 8}
                textAnchor="middle"
                fontSize={10}
                fill={theme.jiaying.text.tertiary}
              >
                {px[px.length - 1]?.date ?? ''}
              </text>
            )}
          </svg>
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        {deltaText !== null && (
          <Typography variant="caption" color="text.secondary" data-testid="ma-curve-delta">
            区间变化 {deltaText}
          </Typography>
        )}
        {last !== undefined && (
          <Typography variant="caption" color="text.secondary" data-testid="ma-curve-current-text">
            当前估值 {formatMoneyText(last.value as Cents, currency)}（{last.valuationDate}）
          </Typography>
        )}
      </Stack>

      {state === 'single' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {CURVE_SINGLE_HINT}
        </Typography>
      )}
    </Box>
  );
}
